// subscription.api.js

function getSubscribeConf() {
  const conf = globalConf(globalSubscribeConf);
  if (!conf) throw new Error('SubscribeConf not found (globalSubscribeConf)');
  return conf;
}

function getHeaders(extra = {}) {
  const conf = getSubscribeConf();

  return {
    Accept: 'application/json',
    'Access-Hash': conf.customer_hash,
    ...extra,
  };
}

function getBaseUrl() {
  const conf = getSubscribeConf();
  return `https://${conf.subscribe_api_domain}/apps/subscriptions`;
}

function toYmd(dateStr) {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr.split('.').reverse().join('-');
  return dateStr;
}

function normalizePhone(phone) {
  return String(phone || '').replace(/[^\d+]/g, '');
}

async function request(path, options = {}) {
  const {
    method = 'GET',
    headers = {},
    body,
    signal,
    credentials = 'include',
  } = options;

  const url = `${getBaseUrl()}${path}`;

  const res = await fetch(url, {
    method,
    headers: getHeaders(headers),
    body,
    signal,
    credentials,
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  let data = null;

  try {
    data = isJson ? await res.json() : await res.text();
  } catch (e) {
    data = null;
  }

  if (!res.ok) {
    const err = new Error(
      data?.message || data?.error || `Request failed: ${method} ${path}`
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

/**
 * GET /check?customer_id=...
 */
export async function checkSubscription(customerId, options = {}) {
  return request(`/check?customer_id=${encodeURIComponent(customerId)}`, {
    method: 'GET',
    signal: options.signal,
  });
}

/**
 * GET /get-data?customer_id=...
 */
export async function getSubscriptionData(customerId, options = {}) {
  return request(`/get-data?customer_id=${encodeURIComponent(customerId)}`, {
    method: 'GET',
    signal: options.signal,
  });
}

/**
 * POST /create або /update
 */
export async function submitSubscription(mode, payload, options = {}) {
  if (mode !== 'create' && mode !== 'update') {
    throw new Error(`Unknown mode: ${mode}`);
  }

  const fd = new FormData();

  fd.append('customer_id', String(payload.customer_id));
  fd.append('active', '1');
  fd.append('notify_days', String(payload.notify_days ?? ''));
  fd.append('months', String(payload.months ?? ''));
  fd.append('pay_type', String(payload.pay_type ?? ''));
  fd.append('delivery_address', String(payload.delivery_address || ''));
  fd.append('json', JSON.stringify(payload.json || {}));
  fd.append('start_date', toYmd(payload.start_date));
  fd.append('end_date', toYmd(payload.end_date));
  fd.append('phone', normalizePhone(payload.phone));
  fd.append('note', String(payload.note ?? ''));
  fd.append('source', 'web');

  const products = Array.isArray(payload.products) ? payload.products : [];
  products.forEach((p, i) => {
    fd.append(`products[${i}][product_id]`, String(p.product_id ?? ''));
    fd.append(`products[${i}][variant_id]`, String(p.variant_id ?? ''));
    fd.append(`products[${i}][amount]`, String(p.amount ?? ''));
  });

  return request(`/${mode}`, {
    method: 'POST',
    body: fd,
    signal: options.signal,
  });
}

/**
 * POST /cancel
 */
export async function cancelSubscription(customerId, options = {}) {
  return request('/cancel', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      customer_id: String(customerId),
    }),
    signal: options.signal,
  });
}

export const subscriptionApiUtils = {
  normalizePhone,
  toYmd,
};